/**
 * TUVI - Modulo de Alertas y Correos Automaticos
 * CRUD para gestion de alertas con envio de prueba
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './AlertasAdmin.css';

const CATEGORIAS = [
  { value: 'proyecto_inicio', label: 'Dias desde inicio del proyecto' },
  { value: 'proyecto_fin', label: 'Dias antes de fin del proyecto' },
  { value: 'evento', label: 'Al ocurrir un evento' },
  { value: 'recurrente', label: 'Recurrente (cada X dias)' },
];

const TIPOS_DIAS = [
  { value: 'habiles', label: 'Dias habiles' },
  { value: 'calendario', label: 'Dias calendario' },
];

const VARIABLES = [
  '{proyecto_nombre}', '{cliente}', '{apartamento}', '{fecha_inicio}',
  '{fecha_fin}', '{dias_habiles}', '{dias_restantes}', '{valor_proyecto}', '{estado}',
];

export default function AlertasAdmin() {
  const { success, error } = useNotification();
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Modal prueba
  const [modalPrueba, setModalPrueba] = useState(null);
  const [emailPrueba, setEmailPrueba] = useState('');
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);

  // Historial
  const [historial, setHistorial] = useState([]);
  const [historialAbierto, setHistorialAbierto] = useState(null);

  const [form, setForm] = useState({
    tipo_alerta: '', nombre: '', activo: true, destinatarios_default: '',
    dias_habiles: 10, categoria: 'proyecto_inicio', tipo_dias: 'habiles',
    plantilla_asunto: '', plantilla_cuerpo: '', descripcion: '',
  });

  const cargarAlertas = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(`{
        alertasConfiguracion {
          id tipo_alerta nombre activo destinatarios_default dias_habiles
          categoria tipo_dias plantilla_asunto plantilla_cuerpo descripcion
          total_enviadas ultima_enviada
        }
      }`);
      setAlertas(d.alertasConfiguracion || []);
    } catch { error('Error cargando alertas'); }
    finally { setLoading(false); }
  }, [error]);

  useEffect(() => { cargarAlertas(); }, [cargarAlertas]);

  const resetForm = () => {
    setForm({
      tipo_alerta: '', nombre: '', activo: true, destinatarios_default: '',
      dias_habiles: 10, categoria: 'proyecto_inicio', tipo_dias: 'habiles',
      plantilla_asunto: '', plantilla_cuerpo: '', descripcion: '',
    });
    setEditandoId(null);
    setModalAbierto(false);
  };

  const abrirCrear = () => { resetForm(); setModalAbierto(true); };

  const abrirEditar = (alerta) => {
    setForm({
      tipo_alerta: alerta.tipo_alerta, nombre: alerta.nombre || '',
      activo: alerta.activo, destinatarios_default: alerta.destinatarios_default || '',
      dias_habiles: alerta.dias_habiles || 0, categoria: alerta.categoria || 'proyecto_inicio',
      tipo_dias: alerta.tipo_dias || 'habiles',
      plantilla_asunto: alerta.plantilla_asunto || '', plantilla_cuerpo: alerta.plantilla_cuerpo || '',
      descripcion: alerta.descripcion || '',
    });
    setEditandoId(alerta.id);
    setModalAbierto(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { error('El nombre es obligatorio'); return; }
    if (!form.tipo_alerta.trim()) { error('El identificador es obligatorio'); return; }
    setGuardando(true);
    try {
      if (editandoId) {
        const r = await client.mutate(`mutation($id: Int!, $input: AlertaConfigInput!) { editarAlerta(id: $id, input: $input) { success message } }`, { id: editandoId, input: form });
        if (r.editarAlerta.success) { success('Alerta actualizada'); resetForm(); cargarAlertas(); }
        else error(r.editarAlerta.message);
      } else {
        const r = await client.mutate(`mutation($input: AlertaConfigInput!) { crearAlerta(input: $input) { success message } }`, { input: form });
        if (r.crearAlerta.success) { success('Alerta creada'); resetForm(); cargarAlertas(); }
        else error(r.crearAlerta.message);
      }
    } catch { error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Eliminar esta alerta?')) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarAlerta(id: $id) { success message } }`, { id });
      if (r.eliminarAlerta.success) { success('Alerta eliminada'); cargarAlertas(); }
    } catch { error('Error al eliminar'); }
  };

  const toggleActivo = async (id, activo) => {
    try {
      await client.mutate(`mutation($id: Int!, $activo: Boolean!) { toggleAlerta(id: $id, activo: $activo) { success } }`, { id, activo });
      cargarAlertas();
    } catch { error('Error al cambiar estado'); }
  };

  const enviarPrueba = async () => {
    if (!emailPrueba.trim() || !emailPrueba.includes('@')) { error('Ingresa un correo valido'); return; }
    setEnviandoPrueba(true);
    try {
      const r = await client.mutate(`mutation($id: Int!, $email: String!) { enviarAlertaPrueba(id: $id, email: $email) { success message } }`, { id: modalPrueba.id, email: emailPrueba });
      if (r.enviarAlertaPrueba.success) { success(r.enviarAlertaPrueba.message); setModalPrueba(null); setEmailPrueba(''); }
      else error(r.enviarAlertaPrueba.message);
    } catch { error('Error al enviar prueba'); }
    finally { setEnviandoPrueba(false); }
  };

  const verHistorial = async (alerta) => {
    try {
      const d = await client.query(`query($tipo: String!) { alertasEnviadas(tipo_alerta: $tipo, limit: 20) { id proyecto_nombre cliente destinatarios fecha_envio estado mensaje_error } }`, { tipo: alerta.tipo_alerta });
      setHistorial(d.alertasEnviadas || []);
      setHistorialAbierto(alerta);
    } catch { error('Error cargando historial'); }
  };

  const getCategoriaLabel = (cat) => CATEGORIAS.find(c => c.value === cat)?.label || cat;

  const insertarVariable = (variable) => {
    setForm(prev => ({ ...prev, plantilla_cuerpo: (prev.plantilla_cuerpo || '') + ' ' + variable }));
  };

  if (loading) return <div className="al-loading"><div className="al-spinner"></div><p>Cargando alertas...</p></div>;

  return (
    <div className="al-container">
      <div className="al-header">
        <div>
          <h2 className="al-title">Alertas y Correos Automaticos</h2>
          <p className="al-subtitle">Administra las notificaciones automaticas del sistema</p>
        </div>
        <button className="al-btn al-btn-primary" onClick={abrirCrear}>+ Nueva Alerta</button>
      </div>

      {/* Lista de alertas */}
      <div className="al-grid">
        {alertas.map(a => (
          <div key={a.id} className={`al-card ${a.activo ? '' : 'al-card-inactiva'}`}>
            <div className="al-card-header">
              <div className="al-card-title-row">
                <h3 className="al-card-title">{a.nombre || a.tipo_alerta}</h3>
                <label className="al-switch">
                  <input type="checkbox" checked={a.activo} onChange={() => toggleActivo(a.id, !a.activo)} />
                  <span className="al-slider"></span>
                </label>
              </div>
              <span className="al-badge al-badge-cat">{getCategoriaLabel(a.categoria)}</span>
            </div>

            <div className="al-card-body">
              {a.descripcion && <p className="al-card-desc">{a.descripcion}</p>}
              <div className="al-card-meta">
                {a.dias_habiles > 0 && (
                  <span className="al-meta-item">
                    <strong>{a.dias_habiles}</strong> {a.tipo_dias === 'habiles' ? 'dias habiles' : 'dias calendario'}
                  </span>
                )}
                <span className="al-meta-item al-meta-id">ID: {a.tipo_alerta}</span>
              </div>

              <div className="al-card-destinatarios">
                <span className="al-dest-label">Destinatarios:</span>
                <div className="al-dest-chips">
                  {(a.destinatarios_default || '').split(',').filter(Boolean).map((email, i) => (
                    <span key={i} className="al-chip">{email.trim()}</span>
                  ))}
                  {!a.destinatarios_default && <span className="al-chip al-chip-empty">Sin destinatarios</span>}
                </div>
              </div>

              <div className="al-card-stats">
                <span>{a.total_enviadas || 0} enviadas</span>
                {a.ultima_enviada && <span>Ultima: {a.ultima_enviada}</span>}
              </div>
            </div>

            <div className="al-card-actions">
              <button className="al-btn al-btn-sm al-btn-outline" onClick={() => abrirEditar(a)}>Editar</button>
              <button className="al-btn al-btn-sm al-btn-test" onClick={() => { setModalPrueba(a); setEmailPrueba(''); }}>Enviar Prueba</button>
              <button className="al-btn al-btn-sm al-btn-hist" onClick={() => verHistorial(a)}>Historial</button>
              <button className="al-btn al-btn-sm al-btn-danger" onClick={() => eliminar(a.id)}>Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {alertas.length === 0 && <div className="al-empty"><p>No hay alertas configuradas. Crea la primera.</p></div>}

      {/* Modal Crear/Editar */}
      {modalAbierto && (
        <div className="al-modal-overlay" onClick={() => resetForm()}>
          <div className="al-modal" onClick={e => e.stopPropagation()}>
            <div className="al-modal-header">
              <h3>{editandoId ? 'Editar Alerta' : 'Nueva Alerta'}</h3>
              <button className="al-modal-close" onClick={resetForm}>&times;</button>
            </div>
            <div className="al-modal-body">
              <div className="al-form-row">
                <div className="al-form-group">
                  <label>Nombre *</label>
                  <input type="text" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Alerta video avance 10 dias" />
                </div>
                <div className="al-form-group">
                  <label>Identificador unico *</label>
                  <input type="text" value={form.tipo_alerta} onChange={e => setForm({ ...form, tipo_alerta: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') })} placeholder="ej: video_avance_10_dias" disabled={!!editandoId} />
                </div>
              </div>

              <div className="al-form-row">
                <div className="al-form-group">
                  <label>Categoria de disparo</label>
                  <select value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                    {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="al-form-group">
                  <label>Dias</label>
                  <input type="number" value={form.dias_habiles} onChange={e => setForm({ ...form, dias_habiles: parseInt(e.target.value) || 0 })} min="0" />
                </div>
                <div className="al-form-group">
                  <label>Tipo de dias</label>
                  <select value={form.tipo_dias} onChange={e => setForm({ ...form, tipo_dias: e.target.value })}>
                    {TIPOS_DIAS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="al-form-group">
                <label>Destinatarios (separados por coma)</label>
                <input type="text" value={form.destinatarios_default} onChange={e => setForm({ ...form, destinatarios_default: e.target.value })} placeholder="correo1@empresa.com, correo2@empresa.com" />
              </div>

              <div className="al-form-group">
                <label>Asunto del correo</label>
                <input type="text" value={form.plantilla_asunto} onChange={e => setForm({ ...form, plantilla_asunto: e.target.value })} placeholder="Alerta: {proyecto_nombre} - {cliente}" />
              </div>

              <div className="al-form-group">
                <label>Cuerpo del correo (HTML permitido)</label>
                <div className="al-variables-bar">
                  {VARIABLES.map(v => (
                    <button key={v} type="button" className="al-var-btn" onClick={() => insertarVariable(v)}>{v}</button>
                  ))}
                </div>
                <textarea rows={6} value={form.plantilla_cuerpo} onChange={e => setForm({ ...form, plantilla_cuerpo: e.target.value })}
                  placeholder={'<p>El proyecto <strong>{proyecto_nombre}</strong> del cliente <strong>{cliente}</strong> (Apto. {apartamento}) ha alcanzado <strong>{dias_habiles} dias habiles</strong> desde su inicio.</p>'} />
              </div>

              <div className="al-form-group">
                <label>Descripcion interna</label>
                <input type="text" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="Nota interna sobre esta alerta..." />
              </div>
            </div>
            <div className="al-modal-footer">
              <button className="al-btn al-btn-secondary" onClick={resetForm}>Cancelar</button>
              <button className="al-btn al-btn-primary" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : editandoId ? 'Actualizar' : 'Crear Alerta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Prueba */}
      {modalPrueba && (
        <div className="al-modal-overlay" onClick={() => setModalPrueba(null)}>
          <div className="al-modal al-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="al-modal-header">
              <h3>Enviar Correo de Prueba</h3>
              <button className="al-modal-close" onClick={() => setModalPrueba(null)}>&times;</button>
            </div>
            <div className="al-modal-body">
              <p className="al-prueba-info">Se enviara un correo con datos de ejemplo para la alerta: <strong>{modalPrueba.nombre}</strong></p>
              <div className="al-form-group">
                <label>Correo destino</label>
                <input type="email" value={emailPrueba} onChange={e => setEmailPrueba(e.target.value)}
                  placeholder="tucorreo@ejemplo.com" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') enviarPrueba(); }} />
              </div>
            </div>
            <div className="al-modal-footer">
              <button className="al-btn al-btn-secondary" onClick={() => setModalPrueba(null)}>Cancelar</button>
              <button className="al-btn al-btn-test" onClick={enviarPrueba} disabled={enviandoPrueba}>
                {enviandoPrueba ? 'Enviando...' : 'Enviar Prueba'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historial */}
      {historialAbierto && (
        <div className="al-modal-overlay" onClick={() => setHistorialAbierto(null)}>
          <div className="al-modal" onClick={e => e.stopPropagation()}>
            <div className="al-modal-header">
              <h3>Historial: {historialAbierto.nombre}</h3>
              <button className="al-modal-close" onClick={() => setHistorialAbierto(null)}>&times;</button>
            </div>
            <div className="al-modal-body">
              {historial.length === 0 ? (
                <p className="al-empty-hist">No hay envios registrados para esta alerta</p>
              ) : (
                <table className="al-hist-table">
                  <thead>
                    <tr><th>Proyecto</th><th>Cliente</th><th>Destinatarios</th><th>Fecha</th><th>Estado</th></tr>
                  </thead>
                  <tbody>
                    {historial.map(h => (
                      <tr key={h.id}>
                        <td>{h.proyecto_nombre || '-'}</td>
                        <td>{h.cliente || '-'}</td>
                        <td className="al-hist-dest">{h.destinatarios}</td>
                        <td>{h.fecha_envio ? new Date(h.fecha_envio).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                        <td><span className={`al-estado-badge al-estado-${h.estado}`}>{h.estado}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="al-modal-footer">
              <button className="al-btn al-btn-secondary" onClick={() => setHistorialAbierto(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
